/**
 * Booth event telemetry (Plan 01) — renderer-side event layer.
 *
 * Distinct from the interval heartbeat (sendAppSettingsTelemetry) and print-job
 * logging (printEventLogger). This store records coalesced, incident-level
 * *actions* and POSTs them in batches to /api/booth-events/batch.
 *
 * Noise control is the whole point — callers feed raw signals (modal opened,
 * camera dropped, session ended) and this store collapses them:
 *   - settings_session : one event per open→close; re-opens within 60s merge
 *                        (count++), carries accumulated durationMs + unlocked + tabs.
 *   - camera_incident  : connected→disconnected debounced 10s; one event with a
 *     printer_incident   resolve-patch (resolvedAt/downtimeMs) on reconnect.
 *   - session_ended    : exactly one event per session, with computed outcome.
 *
 * The queue persists (offline booths flush on reconnect); server upserts are
 * idempotent by clientEventId so re-POSTing is safe.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { generateUUID } from '@renderer/utils/idUtils'
import { useSettingsStore } from './settingsStore'
import { checkNetworkStatus } from '@renderer/utils/pipeline/bridgeApi'
import { isSimulationActive } from '@renderer/lib/simulation'
import { reportSessionActive } from '@renderer/lib/runtime/updater'
import { restartEventId, type RestartReport } from '@renderer/utils/telemetry/rendererLifecycle'

const API_BASE_URL =
  (typeof process !== 'undefined' ? process.env?.VITE_API_URL : undefined) ||
  import.meta.env.VITE_API_URL ||
  'https://dreamboothstudio.com'

const FLUSH_INTERVAL_MS = 30_000
const SETTINGS_COALESCE_MS = 60_000
const DEVICE_DEBOUNCE_MS = 10_000

/**
 * Hard cap on the durable queue.
 *
 * The queue persists to localStorage and a failed flush deliberately KEEPS its events to
 * retry later, so an offline booth accumulates. That was harmless while every event type
 * was rare (a settings session, an incident, a session end). camera_connect is the first
 * PER-ATTEMPT event, so a booth that cannot reach the backend and keeps retrying connects
 * would grow this without limit — an unbounded write path, which is the same shape of
 * mistake as the goodbye-report recursion, just with storage instead of stack.
 *
 * 500 is far above anything a healthy booth reaches between flushes and small enough to
 * stay trivial in localStorage.
 */
const MAX_QUEUED_EVENTS = 500
const MAX_BATCH = 50
const FLUSH_RETRY_DELAYS_MS = [1000, 2000, 4000]

export type BoothEventType =
  | 'settings_session'
  | 'camera_connect'
  | 'camera_incident'
  | 'printer_incident'
  | 'session_ended'
  | 'license_incident'
  | 'camera_preset_apply'
  | 'renderer_restart'

/**
 * Licence lifecycle actions (license_incident.metadata.action). Emitted so the
 * dashboard can finally SEE why booths lose/regain their activation token —
 * the renderer used to only console.warn these, which never leaves the machine.
 */
export type LicenseIncidentAction =
  | 'invalid_retained' // validate verdict rejected the token; token kept (PR #125)
  | 'moved' // operator confirmed moving the licence to this device
  | 'restored' // one-click re-apply of the stored token succeeded
  | 'cleared_account_switch' // a different account signed in; token cleared

export type BoothEventOutcome =
  | 'completed'
  | 'abandoned_pre_payment'
  | 'abandoned_post_payment'
  | 'capture_error'
  | 'print_error'

export interface QueuedBoothEvent {
  eventType: BoothEventType
  deviceId: string
  projectId: string
  sessionId?: string
  clientEventId: string
  timestamp: string
  outcome?: BoothEventOutcome
  count?: number
  durationMs?: number
  resolvedAt?: string
  severity?: 'info' | 'warn' | 'error'
  metadata?: Record<string, unknown>
}

interface BoothEventState {
  queue: QueuedBoothEvent[]
  enqueue: (ev: QueuedBoothEvent) => void
  flush: () => Promise<void>
  // context
  setProjectContext: (projectId: string | undefined) => void
  // settings
  beginSettingsSession: () => void
  markBoothUnlocked: () => void
  markSettingsUnlocked: () => void
  markSettingsTab: (tab: string) => void
  endSettingsSession: () => void
  // device connectivity
  setCameraConnected: (connected: boolean) => void
  /**
   * `detail` rides on the incident it opens (e.g. the probe's status and the
   * printer name), so a page can say "offline" vs "error" instead of just
   * "printer".
   */
  setPrinterConnected: (connected: boolean, detail?: Record<string, unknown>) => void
  /**
   * How a print ended in the Windows queue (shell print-queue watcher). A stuck
   * or errored job opens a printer_incident (metadata.kind job_stuck/job_error)
   * the server pages on; the next drained print on that printer resolves it.
   */
  reportPrintQueueOutcome: (result: PrintQueueResult) => void
  // session lifecycle
  startBoothSession: (sessionId: string) => void
  markPaymentReached: () => void
  endBoothSession: (
    sessionId: string,
    // 'abandoned' is refined to pre/post-payment here using the tracked flag.
    outcome: BoothEventOutcome | 'abandoned',
    metadata?: Record<string, unknown>
  ) => void
  // licence lifecycle
  recordLicenseIncident: (
    action: LicenseIncidentAction,
    detail?: { reason?: string | null }
  ) => void
  // camera preset apply outcome
  recordCameraPresetApply: (detail: CameraPresetApplyDetail) => void
  // outcome of one attempt to bind a camera
  recordCameraConnect: (detail: CameraConnectDetail) => void
  // the previous renderer died without closing (utils/telemetry/rendererLifecycle)
  recordRendererRestart: (detail: RestartReport) => void
}

/**
 * One camera-preset apply, as it actually went. Emitted once per apply — not
 * only on failure — because the question the dashboard needs to answer is
 * whether a bulk config write has EVER left a booth's camera wedged, and that
 * needs the successful denominator too.
 */
/**
 * The outcome of one attempt to bind a camera.
 *
 * This is the booth's biggest blind spot. `logCameraActivity` starts with
 * `if (!session.id) return`, so everything at Booth Setup — the first connect of the day,
 * its retries, the USB reset, an outright failure — is never recorded anywhere off the
 * booth. The moment an operator has least visibility is the moment we log least, and
 * "the camera would not connect this morning" has been unanswerable from data.
 *
 * Emitted for EVERY attempt, not only failures: the funnel needs its denominator. Without
 * the successes, "12 failures" says nothing about whether a fleet of 1,000 booths is
 * healthy or on fire.
 *
 * `recovered` is the interesting one — the first bind failed and the USB reset
 * (the software equivalent of replugging) rescued it. That count is the direct measure of
 * whether the stale-session self-heal is earning its keep in the field.
 */
export interface CameraConnectDetail {
  outcome: 'ok' | 'recovered' | 'failed'
  cameraKey: 'primary' | 'secondary'
  deviceType: 'webcam' | 'camera'
  durationMs: number
  /** True when the USB reset path ran before the bind succeeded. */
  usedUsbReset: boolean
  backend?: string | null
  camera?: string | null
  /** Decoded failure message, already free of any pointer noise. */
  reason?: string | null
}

export interface CameraPresetApplyDetail {
  /**
   * Which mechanism wrote: an operator-chosen preset, or the automatic booth
   * baseline (flag `camera_baseline_settings`). They share this event type
   * because both answer the same question — has a config write ever left a
   * booth's camera wedged — but they must stay separable when reading it.
   */
  kind?: 'preset' | 'baseline'
  cameraKey: 'primary' | 'secondary'
  status: string
  tier: string
  applied: number
  adjusted: number
  skipped: number
  failed: number
  durationMs: number
  cameraHealthy: boolean
  sourceProjectId?: string
  cameraModel?: string
  reason?: string
}

// ---- Ephemeral (non-persisted) coalescing/debounce state, kept off the store
// so timer handles and in-flight incident bookkeeping never get serialized. ----

let currentProjectId: string | undefined

let flushTimer: ReturnType<typeof setInterval> | null = null
let flushing = false

interface SettingsAccumulator {
  firstOpenedAt: number
  lastOpenedAt: number
  accumulatedMs: number
  count: number
  unlocked: boolean
  tabsTouched: Set<string>
  closeTimer: ReturnType<typeof setTimeout> | null
}
let settingsAcc: SettingsAccumulator | null = null
// A correct booth-unlock PIN fires just BEFORE the settings modal mounts, so we
// bridge it: the next settings session begun within this window is "unlocked".
let pendingUnlockAt: number | null = null

interface DeviceIncidentState {
  connected: boolean
  debounceTimer: ReturnType<typeof setTimeout> | null
  // when connectivity first dropped — measured from the FIRST detection (not
  // when the debounce fires) so a 30s outage reports ~30000ms downtime.
  disconnectedAt: number | null
  // set once the incident is "open" (survived debounce + event enqueued)
  openClientEventId: string | null
}
const cameraState: DeviceIncidentState = {
  connected: true,
  debounceTimer: null,
  disconnectedAt: null,
  openClientEventId: null
}
const printerState: DeviceIncidentState = {
  connected: true,
  debounceTimer: null,
  disconnectedAt: null,
  openClientEventId: null
}

interface ActiveSession {
  sessionId: string
  paymentReached: boolean
}
let activeSession: ActiveSession | null = null

/** A guest session started on Welcome and not ended yet (view flow only). */
export function isBoothSessionActive(): boolean {
  return activeSession !== null
}

// license_incident noise control: validate runs on every boot / Welcome
// pre-flight, so a persistently-invalid token would emit the same
// invalid_retained event over and over. Only re-emit when the (action, reason)
// changes or after a cool-down. Ephemeral by design — a restart may emit one
// extra event, which is fine for incident-grade data.
const LICENSE_REEMIT_MS = 6 * 60 * 60 * 1000 // 6h
let lastLicenseKey: string | null = null
let lastLicenseAt = 0
// Guard so a session emits session_ended at most once even if cleanup runs twice.
const endedSessions = new Set<string>()

/** Print-queue incidents still open, per printer (see reportPrintQueueOutcome). */
const openQueueIncidents = new Map<
  string,
  { clientEventId: string; projectId: string; sessionId?: string; timestamp: string; metadata: Record<string, unknown> }
>()

/** What the shell's print-queue watcher reports; mirrors preload PrintQueueOutcome. */
export interface PrintQueueResult {
  outcome: 'printed' | 'stuck' | 'error' | 'unknown'
  reason: string
  printerName: string
  waitedMs: number
  contexts: { sessionId?: string; projectId?: string }[]
  last?: Record<string, unknown>
}

function resolveDeviceId(): string | undefined {
  return useSettingsStore.getState().deviceId
}

function resolveProjectId(): string | undefined {
  if (currentProjectId) return currentProjectId
  // Fallback to the persisted default project on the booth.
  const s = useSettingsStore.getState() as { defaultProjectId?: string }
  return s.defaultProjectId
}

function ensureFlushTimer(): void {
  if (flushTimer || typeof window === 'undefined') return
  flushTimer = setInterval(() => {
    void useBoothEventStore.getState().flush()
  }, FLUSH_INTERVAL_MS)
}

export const useBoothEventStore = create<BoothEventState>()(
  persist(
    (set, get) => ({
      queue: [],

      enqueue: (ev) => {
        // Sim Mode is read-only. Guarding enqueue (not just flush) matters because
        // the queue persists to localStorage — a simulated event would otherwise
        // survive the reload that exits sim and flush as real telemetry.
        if (isSimulationActive()) {
          return
        }
        if (!ev.deviceId) {
          console.warn('[BoothEvent] dropping event with no deviceId', ev.eventType)
          return
        }
        if (!ev.projectId) {
          console.warn('[BoothEvent] dropping event with no projectId', ev.eventType)
          return
        }
        set((state) => {
          const queue = [...state.queue, ev]
          if (queue.length <= MAX_QUEUED_EVENTS) return { queue }
          // Over capacity: shed the oldest routine event first so a long offline stretch
          // costs us statistics rather than the incidents and failures worth keeping.
          const victim =
            queue.findIndex((q) => (q.severity ?? 'info') === 'info') !== -1
              ? queue.findIndex((q) => (q.severity ?? 'info') === 'info')
              : 0
          console.warn(
            `[BoothEvent] queue at capacity (${MAX_QUEUED_EVENTS}), dropping ${queue[victim].eventType}`
          )
          return { queue: queue.filter((_, i) => i !== victim) }
        })
        ensureFlushTimer()
      },

      flush: async () => {
        if (flushing) return
        const initialQueue = get().queue
        if (initialQueue.length === 0) return

        const online = await checkNetworkStatus()
        if (!online) return

        flushing = true
        try {
          // Loop so a single flush drains a backlog (e.g. after reconnect),
          // batching at most MAX_BATCH per request.
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const batch = get().queue.slice(0, MAX_BATCH)
            if (batch.length === 0) break

            let sent = false
            for (let attempt = 0; attempt <= FLUSH_RETRY_DELAYS_MS.length; attempt++) {
              try {
                const res = await fetch(`${API_BASE_URL}/api/booth-events/batch`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  cache: 'no-cache',
                  body: JSON.stringify({ events: batch })
                })
                if (!res.ok) {
                  throw new Error(`${res.status} ${res.statusText}`)
                }
                sent = true
                break
              } catch (err) {
                if (attempt < FLUSH_RETRY_DELAYS_MS.length) {
                  await new Promise((r) => setTimeout(r, FLUSH_RETRY_DELAYS_MS[attempt]))
                } else {
                  console.warn('[BoothEvent] flush failed, will retry later:', err)
                }
              }
            }

            if (!sent) break // keep batch in queue; periodic timer retries

            // Drop exactly the events we sent (matched by clientEventId).
            const sentIds = new Set(batch.map((e) => e.clientEventId + '|' + (e.resolvedAt ?? '')))
            set((state) => ({
              queue: state.queue.filter(
                (e) => !sentIds.has(e.clientEventId + '|' + (e.resolvedAt ?? ''))
              )
            }))
          }
        } finally {
          flushing = false
        }
      },

      setProjectContext: (projectId) => {
        if (projectId) currentProjectId = projectId
      },

      // ---- settings_session ------------------------------------------------
      beginSettingsSession: () => {
        // Consume a recent booth-unlock PIN (fired just before this mount).
        const unlockedViaPin =
          pendingUnlockAt != null && Date.now() - pendingUnlockAt < SETTINGS_COALESCE_MS
        pendingUnlockAt = null

        if (settingsAcc && settingsAcc.closeTimer) {
          // Re-open within the coalesce window → merge into the same incident.
          clearTimeout(settingsAcc.closeTimer)
          settingsAcc.closeTimer = null
          settingsAcc.count += 1
          settingsAcc.lastOpenedAt = Date.now()
          if (unlockedViaPin) settingsAcc.unlocked = true
          return
        }
        settingsAcc = {
          firstOpenedAt: Date.now(),
          lastOpenedAt: Date.now(),
          accumulatedMs: 0,
          count: 1,
          unlocked: unlockedViaPin,
          tabsTouched: new Set(),
          closeTimer: null
        }
      },

      // Operator entered a correct PIN to unlock the booth (exit kiosk /
      // open protected settings). Recorded onto the in-flight or next session.
      markBoothUnlocked: () => {
        if (settingsAcc) {
          settingsAcc.unlocked = true
        } else {
          pendingUnlockAt = Date.now()
        }
      },

      markSettingsUnlocked: () => {
        if (settingsAcc) settingsAcc.unlocked = true
      },

      markSettingsTab: (tab) => {
        if (settingsAcc && tab) settingsAcc.tabsTouched.add(tab)
      },

      endSettingsSession: () => {
        if (!settingsAcc) return
        settingsAcc.accumulatedMs += Date.now() - settingsAcc.lastOpenedAt
        if (settingsAcc.closeTimer) clearTimeout(settingsAcc.closeTimer)
        settingsAcc.closeTimer = setTimeout(() => {
          const acc = settingsAcc
          settingsAcc = null
          if (!acc) return
          get().enqueue({
            eventType: 'settings_session',
            deviceId: resolveDeviceId() ?? '',
            projectId: resolveProjectId() ?? '',
            clientEventId: generateUUID(),
            timestamp: new Date(acc.firstOpenedAt).toISOString(),
            count: acc.count,
            durationMs: acc.accumulatedMs,
            severity: acc.unlocked ? 'warn' : 'info',
            metadata: {
              unlocked: acc.unlocked,
              tabsTouched: Array.from(acc.tabsTouched)
            }
          })
        }, SETTINGS_COALESCE_MS)
      },

      // ---- camera_incident / printer_incident ------------------------------
      setCameraConnected: (connected) => {
        handleConnectivity('camera_incident', cameraState, connected, get().enqueue)
      },

      setPrinterConnected: (connected, detail) => {
        handleConnectivity('printer_incident', printerState, connected, get().enqueue, detail)
      },

      reportPrintQueueOutcome: (result): void => {
        const printer = result.printerName || '(default)'
        const open = openQueueIncidents.get(printer)
        const lastContext = result.contexts[result.contexts.length - 1] ?? {}
        if (result.outcome === 'printed') {
          if (!open) return
          openQueueIncidents.delete(printer)
          get().enqueue({
            eventType: 'printer_incident',
            deviceId: resolveDeviceId() ?? '',
            projectId: open.projectId,
            sessionId: open.sessionId,
            clientEventId: open.clientEventId,
            timestamp: open.timestamp,
            resolvedAt: new Date().toISOString(),
            durationMs: Date.now() - Date.parse(open.timestamp),
            severity: 'error',
            metadata: open.metadata
          })
          return
        }
        if (result.outcome !== 'stuck' && result.outcome !== 'error') return
        // One open incident per printer: a queue that stays stuck through five
        // more guests is still one problem, not five pages.
        if (open) return
        const incident = {
          clientEventId: generateUUID(),
          projectId: lastContext.projectId || resolveProjectId() || '',
          sessionId: lastContext.sessionId,
          timestamp: new Date(Date.now() - result.waitedMs).toISOString(),
          metadata: {
            device: 'printer',
            kind: result.outcome === 'stuck' ? 'job_stuck' : 'job_error',
            status: result.reason,
            printerName: result.printerName,
            waitedMs: result.waitedMs,
            sessions: result.contexts.map((c) => c.sessionId).filter(Boolean),
            spooler: result.last
          } as Record<string, unknown>
        }
        openQueueIncidents.set(printer, incident)
        get().enqueue({
          eventType: 'printer_incident',
          deviceId: resolveDeviceId() ?? '',
          projectId: incident.projectId,
          sessionId: incident.sessionId,
          clientEventId: incident.clientEventId,
          timestamp: incident.timestamp,
          severity: 'error',
          metadata: incident.metadata
        })
      },

      // ---- session_ended ---------------------------------------------------
      startBoothSession: (sessionId) => {
        if (!sessionId) return
        activeSession = { sessionId, paymentReached: false }
        endedSessions.delete(sessionId)
        // Holds off an auto-update restart for as long as the guest is here.
        reportSessionActive(true)
      },

      markPaymentReached: () => {
        if (activeSession) activeSession.paymentReached = true
      },

      endBoothSession: (sessionId, outcome, metadata) => {
        if (!sessionId) return
        // Emit exactly once per session.
        if (endedSessions.has(sessionId)) return
        endedSessions.add(sessionId)

        const paymentReached =
          activeSession?.sessionId === sessionId ? activeSession.paymentReached : undefined

        // Refine generic abandonment into pre/post-payment.
        const resolvedOutcome: BoothEventOutcome =
          outcome === 'abandoned'
            ? paymentReached
              ? 'abandoned_post_payment'
              : 'abandoned_pre_payment'
            : outcome

        get().enqueue({
          eventType: 'session_ended',
          deviceId: resolveDeviceId() ?? '',
          projectId: resolveProjectId() ?? '',
          sessionId,
          clientEventId: generateUUID(),
          timestamp: new Date().toISOString(),
          outcome: resolvedOutcome,
          severity:
            resolvedOutcome === 'completed'
              ? 'info'
              : resolvedOutcome === 'capture_error' || resolvedOutcome === 'print_error'
                ? 'error'
                : 'warn',
          metadata: { paymentReached, ...(metadata ?? {}) }
        })

        if (activeSession?.sessionId === sessionId) {
          activeSession = null
          // Only for the session that is actually running: a late duplicate end
          // for an older session must not clear the current guest's hold.
          reportSessionActive(false)
        }
        // Session end is a good moment to push everything out.
        void get().flush()
      },

      // ---- license_incident --------------------------------------------------
      recordLicenseIncident: (action, detail) => {
        const reason = detail?.reason ?? undefined

        // Coalesce repeats of the same verdict (see LICENSE_REEMIT_MS above).
        // State transitions (moved/restored/cleared) always emit.
        if (action === 'invalid_retained') {
          const key = `${action}|${reason ?? ''}`
          const now = Date.now()
          if (lastLicenseKey === key && now - lastLicenseAt < LICENSE_REEMIT_MS) return
          lastLicenseKey = key
          lastLicenseAt = now
        } else {
          lastLicenseKey = null
          lastLicenseAt = 0
        }

        get().enqueue({
          eventType: 'license_incident',
          deviceId: resolveDeviceId() ?? '',
          projectId: resolveProjectId() ?? '',
          clientEventId: generateUUID(),
          timestamp: new Date().toISOString(),
          severity: action === 'restored' ? 'info' : action === 'invalid_retained' ? 'error' : 'warn',
          metadata: { action, ...(reason ? { reason } : {}) }
        })
        // Licence trouble is exactly what support needs to see promptly — and a
        // device-move reloads the window right after, so don't wait for the timer.
        void get().flush()
      },

      // ---- camera_connect ----------------------------------------------------
      recordCameraConnect: (detail) => {
        get().enqueue({
          eventType: 'camera_connect',
          deviceId: resolveDeviceId() ?? '',
          projectId: resolveProjectId() ?? '',
          clientEventId: generateUUID(),
          timestamp: new Date().toISOString(),
          durationMs: detail.durationMs,
          // A failed bind is an error; a recovered one is worth noticing but the booth is
          // working, so it must not read as an outage next to camera_incident.
          severity:
            detail.outcome === 'failed' ? 'error' : detail.outcome === 'recovered' ? 'warn' : 'info',
          metadata: { ...detail }
        })
      },

      // ---- renderer_restart -------------------------------------------------
      // One per death, reported by the renderer that came after it. Timestamped
      // when the dead one was last alive, so the timeline puts it where it
      // happened rather than where the booth came back.
      recordRendererRestart: (detail): void => {
        const deviceId = detail.deviceId || resolveDeviceId() || ''
        get().enqueue({
          eventType: 'renderer_restart',
          deviceId,
          projectId: detail.projectId || resolveProjectId() || '',
          ...(detail.sessionId ? { sessionId: detail.sessionId } : {}),
          clientEventId: restartEventId(deviceId, detail.previousBootedAt),
          timestamp: new Date(detail.at).toISOString(),
          durationMs: detail.downMs,
          severity: detail.paid
            ? 'error'
            : detail.kind === 'unclean_exit' && !detail.sessionId
              ? 'info'
              : 'warn',
          metadata: {
            kind: detail.kind,
            detection: detail.detection,
            ...(detail.reason ? { reason: detail.reason } : {}),
            ...(detail.exitCode != null ? { exitCode: detail.exitCode } : {}),
            uptimeMs: detail.uptimeMs,
            paid: detail.paid,
            ...(detail.route ? { route: detail.route } : {}),
            ...(detail.heapUsedMB != null ? { heapUsedMB: detail.heapUsedMB } : {}),
            ...(detail.appVersion ? { appVersion: detail.appVersion } : {}),
            ...(detail.previousAppVersion ? { previousAppVersion: detail.previousAppVersion } : {})
          }
        })
        // A lost paid session is what support needs to hear about promptly.
        void get().flush()
      },

      // ---- camera_preset_apply ----------------------------------------------
      recordCameraPresetApply: (detail) => {
        const bad =
          !detail.cameraHealthy ||
          detail.status === 'timeout' ||
          detail.status === 'aborted'

        get().enqueue({
          eventType: 'camera_preset_apply',
          deviceId: resolveDeviceId() ?? '',
          projectId: resolveProjectId() ?? '',
          clientEventId: generateUUID(),
          timestamp: new Date().toISOString(),
          durationMs: detail.durationMs,
          severity: bad ? 'error' : detail.failed > 0 ? 'warn' : 'info',
          metadata: { ...detail }
        })
        // A camera left unhealthy by a config write is an incident, not a
        // statistic — get it off the booth before anyone reboots it.
        if (bad) void get().flush()
      }
    }),
    {
      name: 'booth-event-queue',
      // Only the durable queue is persisted; timers and in-flight coalescing
      // state are intentionally ephemeral.
      partialize: (state) => ({ queue: state.queue }),
      // Offline booths: a backlog persisted from a previous run should flush as
      // soon as we're back, not wait for the next new event.
      onRehydrateStorage: () => (state) => {
        if (state && state.queue.length > 0) {
          ensureFlushTimer()
          void state.flush()
        }
      }
    }
  )
)

/**
 * Shared connectivity edge-handler for camera/printer. Only acts on real
 * transitions; debounces a drop for 10s so a brief blip never becomes an
 * incident; emits a resolve-patch (same clientEventId) on reconnect.
 */
function handleConnectivity(
  eventType: 'camera_incident' | 'printer_incident',
  st: DeviceIncidentState,
  connected: boolean,
  enqueue: (ev: QueuedBoothEvent) => void,
  detail?: Record<string, unknown>
): void {
  if (connected === st.connected) return // no transition
  st.connected = connected

  if (!connected) {
    // connected → disconnected. Stamp the drop time NOW (first detection) so
    // downtime is measured from the real outage start, then debounce: only if
    // it stays down for 10s do we open a real incident.
    st.disconnectedAt = Date.now()
    if (st.debounceTimer) clearTimeout(st.debounceTimer)
    st.debounceTimer = setTimeout(() => {
      st.debounceTimer = null
      const clientEventId = generateUUID()
      st.openClientEventId = clientEventId
      enqueue({
        eventType,
        deviceId: resolveDeviceId() ?? '',
        projectId: resolveProjectId() ?? '',
        clientEventId,
        timestamp: new Date(st.disconnectedAt ?? Date.now()).toISOString(),
        severity: 'error',
        metadata: { ...detail, device: eventType === 'camera_incident' ? 'camera' : 'printer' }
      })
    }, DEVICE_DEBOUNCE_MS)
    return
  }

  // disconnected → connected
  if (st.debounceTimer) {
    // Reconnected before the debounce fired → it was a blip. Emit nothing.
    clearTimeout(st.debounceTimer)
    st.debounceTimer = null
    st.disconnectedAt = null
    return
  }
  if (st.openClientEventId && st.disconnectedAt != null) {
    // Resolve the open incident: patch the same doc with downtime measured
    // from the first-detected drop to this reconnect.
    const downtimeMs = Date.now() - st.disconnectedAt
    enqueue({
      eventType,
      deviceId: resolveDeviceId() ?? '',
      projectId: resolveProjectId() ?? '',
      clientEventId: st.openClientEventId,
      timestamp: new Date(st.disconnectedAt).toISOString(),
      resolvedAt: new Date().toISOString(),
      durationMs: downtimeMs,
      severity: 'error',
      metadata: { device: eventType === 'camera_incident' ? 'camera' : 'printer' }
    })
    st.openClientEventId = null
    st.disconnectedAt = null
  }
}
